import React, { useState, useEffect } from "react";
import { Table, OverlayTrigger, Tooltip, Form } from "react-bootstrap";

interface DataTableProps {
  headers: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  rows?: any[];
  onAction?: (action: string, row: any) => void;
  onDataChange?: (rows: any[]) => void;
  radioSelection?: boolean;
  onRadioSelect?: (row: any) => void;
  onRowSelect?: (row: any) => void;
  selectedRow?: any;
  isCheckbox?: boolean;
  onCheckboxSelect?: (selectedRows: any[]) => void;
  selectedRowIds?: any;
  disabled?: boolean;
  isLogTable?: boolean;
  isGroupLevel?: boolean;
}

const InvoiceResolveDataTable: React.FC<DataTableProps> = ({
  headers,
  rows = [],
  onAction,
  onDataChange,
  radioSelection = false,
  onRadioSelect,
  selectedRow,
  isCheckbox = false,
  onCheckboxSelect,
  selectedRowIds = [],
  disabled = false,
  isLogTable = false,
  isGroupLevel = false,
}) => {
  const [sortedRows, setSortedRows] = useState<any[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    if (rows?.length > 0) {
      const updated = rows.map((row) => ({
        ...row,
        isActive: row.isActive ?? false,
      }));
      setSortedRows(updated);
    } else {
      setSortedRows([]);
    }
  }, [rows]);

  const groupedRows = isGroupLevel
    ? sortedRows.reduce((acc: any, row: any) => {
        const group = row.level || "Others";
        if (!acc[group]) acc[group] = [];
        acc[group].push(row);
        return acc;
      }, {})
    : {};

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    const updated = newSelectAll ? [...sortedRows] : [];
    setSelectedRows(updated);
    setSelectAll(newSelectAll);
    onCheckboxSelect?.(updated);
  };

  const handleCheckboxChange = (row: any) => {
    const isSelected = selectedRows.some((r) => r.id === row.id);
    const updated = isSelected
      ? selectedRows.filter((r) => r.id !== row.id)
      : [...selectedRows, row];

    setSelectedRows(updated);
    setSelectAll(updated.length === sortedRows.length);
    onCheckboxSelect?.(updated);
  };

  const renderCells = (row: any) =>
    headers.map((header) => {
      if (header.key === "no_Of_Pages") {
        return (
          <td
            key={header.key}
            className="text-center"
            style={{
              textAlign: "center",
            }}
          >
            <input
              className="form-control form-control-sm alignRightText"
              value={row.no_Of_Pages || ""}
              style={{ width: "100%", padding: "4px" }}
              onKeyDown={(e) => {
                if (e.key === " ") e.preventDefault();
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (!/^\d{0,8}(\.\d{0,2})?$/.test(value)) return;
                const updatedRows = sortedRows.map((r) =>
                  r.id === row.id ? { ...r, no_Of_Pages: value } : r,
                );

                setSortedRows(updatedRows);
                onDataChange?.(updatedRows);

                onAction?.("updatePages", {
                  ...row,
                  no_Of_Pages: value,
                });
              }}
            />
          </td>
        );
      }

      if (header.key === "no_of_Lines_missingLineCount") {
        return (
          <td key={header.key}>
            <input
              className="form-control form-control-sm alignRightText"
              value={row.no_of_Lines_missingLineCount || ""}
              style={{ width: "100%", padding: "4px" }}
              onKeyDown={(e) => {
                if (e.key === " ") e.preventDefault();
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (!/^\d{0,8}(\.\d{0,2})?$/.test(value)) return;
                const updatedRows = sortedRows.map((r) =>
                  r.id === row.id
                    ? { ...r, no_of_Lines_missingLineCount: value }
                    : r,
                );

                setSortedRows(updatedRows);
                onDataChange?.(updatedRows);

                onAction?.("updateLines", {
                  ...row,
                  no_of_Lines_missingLineCount: value,
                });
              }}
            />
          </td>
        );
      }

      if (header.key === "isActive") {
        return (
          <td key={header.key} style={{ textAlign: "center" }}>
            <div className="d-flex gap-2 justify-content-center">
              <Form.Check
                type="switch"
                checked={row.isActive}
                onChange={(e) => {
                  const updatedRows = sortedRows.map((r) =>
                    r.id === row.id ? { ...r, isActive: e.target.checked } : r,
                  );

                  setSortedRows(updatedRows);
                  onDataChange?.(updatedRows);

                  onAction?.("toggleActive", {
                    ...row,
                    isActive: e.target.checked,
                  });
                }}
              />
              <span>{row.isActive ? "Active" : "Inactive"}</span>
            </div>
          </td>
        );
      }

      if (header.key === "uploadedOnBy") {
        const [datePart, byPart] =
          row?.uploadedOnBy?.split("|").map((s: string) => s.trim()) || [];

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 500 }}>{datePart}</span>

            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`tooltip-by-${row.id}`}>{byPart}</Tooltip>}
            >
              <span
                className="d-inline-block text-truncate"
                style={{
                  fontSize: "13px",
                  color: "#726d6d",
                  maxWidth: "180px",
                  cursor: "pointer",
                }}
              >
                {byPart}
              </span>
            </OverlayTrigger>
          </div>
        );
      }

      const cellContent = row[header.key];
      return (
        <td
          key={header.key}
          style={{
            padding: header.isNumeric ? "8px 20px 8px 8px !important" : "8px",
            textAlign: header.isNumeric ? "center" : "left",
          }}
        >
          <OverlayTrigger
            placement="auto"
            delay={{ show: 200, hide: 10 }}
            overlay={
              cellContent && cellContent?.length > (isLogTable ? 60 : 15) ? (
                <Tooltip
                  id={`tooltip-${header.key}-${row.id}`}
                  style={{ backgroundColor: "#b3e0ff" }}
                >
                  {cellContent}
                </Tooltip>
              ) : (
                <></>
              )
            }
          >
            <span
              className={`d-inline-block ${!isLogTable ? "text-truncate" : ""}`}
              style={{
                maxWidth: isLogTable ? "none" : "100%",
                cursor: isLogTable ? "default" : "pointer",
              }}
            >
              {cellContent}
            </span>
          </OverlayTrigger>
        </td>
      );
    });

  return (
    <div className="table-responsive border-0 mb-2">
      <Table style={{ tableLayout: "fixed" }}>
        <thead>
          <tr>
            {radioSelection && <th style={{ width: "5%" }} />}

            {isCheckbox && (
              <th style={{ width: "7%", textAlign: "center" }}>
                {isGroupLevel ? (
                  <span>Applied Rate</span>
                ) : (
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                    disabled={disabled}
                  />
                )}
              </th>
            )}

            {headers.map((header) => (
              <th
                key={header.key}
                style={{
                  border: "none",
                  padding: "8px",
                  backgroundColor: "#e0f7fa",
                  color: "#000",
                  cursor: header.sortable ? "pointer" : "default",
                  position: "sticky",
                  top: 0,
                  zIndex: -90,
                  textAlign: ["isActiveDetails"].includes(header.key)
                    ? "center"
                    : "left",
                  ...header.style,
                }}
              >
                <div
                  style={{
                    display: ["isActiveDetails"].includes(header.key)
                      ? "flex"
                      : "flex",

                    justifyContent: ["isActiveDetails"].includes(header.key)
                      ? "space-evenly"
                      : "space-between",

                    alignItems: "center",
                  }}
                >
                  <span>{header.label}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {isGroupLevel ? (
            Object.keys(groupedRows).length > 0 ? (
              Object.entries(groupedRows).map(([groupName, groupRows]: any) => (
                <React.Fragment key={groupName}>
                  <tr>
                    <td
                      colSpan={
                        headers.length +
                        (radioSelection ? 1 : 0) +
                        (isCheckbox ? 1 : 0)
                      }
                      style={{
                        backgroundColor: "#d9e3f0",
                        fontWeight: "bold",
                        padding: "8px",
                      }}
                    >
                      {groupName}
                    </td>
                  </tr>

                  {groupRows.map((row: any, index: number) => (
                    <tr
                      key={row.id}
                      style={{
                        backgroundColor: index % 2 === 0 ? "#f2f2f2" : "#fff",
                      }}
                    >
                      {radioSelection && (
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="radio"
                            checked={
                              selectedRow?.original?.scheduleId ===
                              row.original?.scheduleId
                            }
                            onChange={() => onRadioSelect?.(row)}
                          />
                        </td>
                      )}

                      {isCheckbox && (
                        <td style={{ textAlign: "center" }}>
                          {row.appliedRate === "A" ? (
                            <span
                              style={{
                                width: "22px",
                                height: "22px",
                                backgroundColor: "#28a745",
                                color: "#fff",
                                borderRadius: "50%",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              A
                            </span>
                          ) : (
                            <input
                              type="checkbox"
                              checked={selectedRows.some(
                                (r) => r.id === row.id,
                              )}
                              onChange={() => handleCheckboxChange(row)}
                              disabled={disabled}
                            />
                          )}
                        </td>
                      )}

                      {renderCells(row)}
                    </tr>
                  ))}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={headers.length} style={{ textAlign: "center" }}>
                  No Record Found
                </td>
              </tr>
            )
          ) : sortedRows?.length > 0 ? (
            sortedRows.map((row, index) => (
              <tr
                key={row.id}
                style={{
                  backgroundColor: index % 2 === 0 ? "#f2f2f2" : "#fff",
                }}
              >
                {radioSelection && (
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="radio"
                      checked={
                        selectedRow?.original?.scheduleId ===
                        row.original?.scheduleId
                      }
                      onChange={() => onRadioSelect?.(row)}
                    />
                  </td>
                )}

                {isCheckbox && (
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedRows.some((r) => r.id === row.id)}
                      onChange={() => handleCheckboxChange(row)}
                      disabled={disabled}
                    />
                  </td>
                )}

                {renderCells(row)}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={headers.length} style={{ textAlign: "center" }}>
                No Record Found
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default InvoiceResolveDataTable;
